import { useState } from 'react';
import {
  DEFAULT_GROUND,
  NEEDS,
  NEED_KEYS,
  REQUIRED_CONTENT,
  SKILLS,
  SKILL_KEYS,
  type ActivityContent,
  type NeedKey,
  type PlaceContent,
  type SkillKey,
  type WorldContent,
} from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Switch } from '@london-life/ui/components/switch';
import { Textarea } from '@london-life/ui/components/textarea';
import { ConfirmDialog } from '@london-life/ui/admin/confirm-dialog';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import type { ContentEditor } from './use-content';
import { number } from './form';

// the desks an activity can open instead of taking time
const DESKS = { flights: 'the flights desk', trains: 'the ticket office', ballot: 'the ballot' } as const;

const ID = /^[a-z0-9_]+$/;
const lines = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
const pounds = (pence: number | undefined) => (pence === undefined ? '' : (pence / 100).toFixed(2));
const toPence = (text: string) => {
  const value = Math.round(Number(text) * 100);
  return text.trim() && Number.isFinite(value) && value > 0 ? value : undefined;
};

type Props = {
  editor: ContentEditor;
  place: PlaceContent;
  readOnly: boolean;
  required: boolean;
  onBack: () => void;
  // open the 3D Place Designer for this place
  onDesign: () => void;
};

// One place: its details on the map, what it is like inside (spots, sign, colours, the changing lines) and every
// activity there with its time, money, effects, skills and career. The 3D layout of the props is the Place
// Designer's job (next release); here the spots and activities are tied together.
export function PlaceEditor({ editor, place, readOnly, required, onBack, onDesign }: Props) {
  const working = editor.working!;
  const activities = working.activities.filter((activity) => activity.placeId === place.id);
  const [removing, setRemoving] = useState(false);
  const [resetting, setResetting] = useState(false);
  // the inside this place came with (the game's ready-made design), when it has one
  const readyMade = editor.reply?.designs?.[place.id];
  const isReadyMade = readyMade !== undefined && JSON.stringify(readyMade) === JSON.stringify(place.interior);
  const [adding, setAdding] = useState(false);
  const [newId, setNewId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const change = (fn: (draft: PlaceContent, content: WorldContent) => void) =>
    editor.edit((content) =>
      fn(
        content.places.find((item) => item.id === place.id)!,
        content,
      ),
    );
  const changeActivity = (id: string, fn: (draft: ActivityContent, content: WorldContent) => void) =>
    editor.edit((content) =>
      fn(
        content.activities.find((item) => item.id === id)!,
        content,
      ),
    );
  const spotOf = (activityId: string) => place.interior.spots.find((spot) => spot.activities.includes(activityId));
  const field = (id: string, label: string, input: React.ReactNode, hint?: string) => (
    <div className="admin-field">
      <Label htmlFor={id}>{label}</Label>
      {input}
      {hint && <p className="admin-muted">{hint}</p>}
    </div>
  );
  const addActivity = async () => {
    const id = newId.trim();
    if (!ID.test(id)) throw new Error('The id uses lower-case letters, numbers and _ only (it never changes).');
    if (working.activities.some((item) => item.id === id)) throw new Error('An activity already has that id.');
    if (!newLabel.trim()) throw new Error('Give it a name players see.');
    editor.edit((content) =>
      content.activities.push({ id, placeId: place.id, label: newLabel.trim(), icon: '✨', seconds: 30, effects: {} }),
    );
  };
  return (
    <div className="content-editor">
      <div className="admin-toolbar">
        <Button type="button" variant="outline" onClick={onBack}>
          ‹ All places
        </Button>
        <Button type="button" onClick={onDesign}>
          Design the inside in 3D
        </Button>
        {!readOnly && readyMade && !isReadyMade && (
          <Button type="button" variant="outline" onClick={() => setResetting(true)}>
            Use the ready-made design
          </Button>
        )}
        {!readOnly && !required && (
          <Button type="button" variant="outline" onClick={() => setRemoving(true)}>
            Remove this place
          </Button>
        )}
      </div>

      <fieldset className="admin-panel content-section" disabled={readOnly}>
        <legend>On the map</legend>
        <div className="content-grid">
          {field(
            'place-name',
            'Name',
            <Input
              id="place-name"
              value={place.name}
              maxLength={60}
              onChange={(e) => change((p) => void (p.name = e.target.value))}
            />,
          )}
          {field(
            'place-area',
            'Area',
            <Input
              id="place-area"
              value={place.area}
              maxLength={60}
              onChange={(e) => change((p) => void (p.area = e.target.value))}
            />,
          )}
          {field(
            'place-icon',
            'Icon (an emoji)',
            <Input
              id="place-icon"
              value={place.icon}
              maxLength={16}
              onChange={(e) => change((p) => void (p.icon = e.target.value))}
            />,
          )}
          {field(
            'place-badge',
            'Badge on the map (optional)',
            <Input
              id="place-badge"
              value={place.badge ?? ''}
              maxLength={20}
              onChange={(e) => change((p) => void (p.badge = e.target.value.trim() ? e.target.value : undefined))}
            />,
          )}
          {field(
            'place-lat',
            'Latitude',
            <Input
              id="place-lat"
              type="number"
              step="0.00001"
              value={place.lat}
              onChange={(e) => change((p) => void (p.lat = number(e.target.value, p.lat)))}
            />,
            'Where it really is (from OpenStreetMap). Between 51 and 52.',
          )}
          {field(
            'place-lon',
            'Longitude',
            <Input
              id="place-lon"
              type="number"
              step="0.00001"
              value={place.lon}
              onChange={(e) => change((p) => void (p.lon = number(e.target.value, p.lon)))}
            />,
          )}
          {field(
            'place-order',
            'Order in lists',
            <Input
              id="place-order"
              type="number"
              value={place.sortOrder}
              onChange={(e) => change((p) => void (p.sortOrder = Math.round(number(e.target.value, p.sortOrder))))}
            />,
          )}
          <div className="admin-field content-switches">
            <label>
              <Switch checked={place.visible} onCheckedChange={(on) => change((p) => void (p.visible = on))} /> Shown on
              the map
            </label>
            <label>
              <Switch
                checked={Boolean(place.comingSoon)}
                onCheckedChange={(on) => change((p) => void (p.comingSoon = on || undefined))}
              />{' '}
              Coming soon
            </label>
          </div>
        </div>
        {field(
          'place-description',
          'Description',
          <Textarea
            id="place-description"
            value={place.description}
            maxLength={400}
            onChange={(e) => change((p) => void (p.description = e.target.value))}
          />,
        )}
        {field(
          'place-things',
          'Things to do, shown on the map card (one per line)',
          <Textarea
            id="place-things"
            value={place.things.join('\n')}
            onChange={(e) => change((p) => void (p.things = lines(e.target.value).slice(0, 12)))}
          />,
        )}
      </fieldset>

      <fieldset className="admin-panel content-section" disabled={readOnly}>
        <legend>Inside</legend>
        <div className="content-grid">
          <div className="admin-field content-switches">
            <label>
              <Switch
                checked={place.interior.outdoor}
                onCheckedChange={(on) => change((p) => void (p.interior.outdoor = on))}
              />{' '}
              Outdoors (no walls)
            </label>
            <label>
              <Switch
                checked={place.interior.window}
                onCheckedChange={(on) => change((p) => void (p.interior.window = on))}
              />{' '}
              A window
            </label>
          </div>
          {field(
            'floor-w',
            'Width (metres)',
            <Input
              id="floor-w"
              type="number"
              step="0.5"
              value={place.interior.floor.w}
              onChange={(e) => change((p) => void (p.interior.floor.w = number(e.target.value, p.interior.floor.w)))}
            />,
          )}
          {field(
            'floor-d',
            'Depth (metres)',
            <Input
              id="floor-d"
              type="number"
              step="0.5"
              value={place.interior.floor.d}
              onChange={(e) => change((p) => void (p.interior.floor.d = number(e.target.value, p.interior.floor.d)))}
            />,
          )}
          {field(
            'wall-colour',
            'Walls',
            <input
              id="wall-colour"
              className="content-colour"
              type="color"
              value={place.interior.wallColour}
              onChange={(e) => change((p) => void (p.interior.wallColour = e.target.value))}
            />,
          )}
          {field(
            'floor-colour',
            'Floor',
            <input
              id="floor-colour"
              className="content-colour"
              type="color"
              value={place.interior.floorColour}
              onChange={(e) => change((p) => void (p.interior.floorColour = e.target.value))}
            />,
          )}
          {field(
            'ground-colour',
            'Ground outside',
            <input
              id="ground-colour"
              className="content-colour"
              type="color"
              value={place.interior.ground ?? DEFAULT_GROUND}
              onChange={(e) => change((p) => void (p.interior.ground = e.target.value))}
            />,
          )}
          {field(
            'sign-text',
            'Sign',
            <Input
              id="sign-text"
              value={place.interior.sign?.text ?? ''}
              maxLength={40}
              onChange={(e) =>
                change(
                  (p) =>
                    void (p.interior.sign = e.target.value
                      ? { background: '#1d4ed8', colour: '#fffdf5', ...p.interior.sign, text: e.target.value }
                      : null),
                )
              }
            />,
          )}
          {place.interior.sign &&
            field(
              'sign-background',
              'Sign colour',
              <input
                id="sign-background"
                className="content-colour"
                type="color"
                value={place.interior.sign.background}
                onChange={(e) =>
                  change((p) => void (p.interior.sign = { ...p.interior.sign!, background: e.target.value }))
                }
              />,
            )}
        </div>
        {field(
          'place-vibes',
          'The changing line under the name (one per line)',
          <Textarea
            id="place-vibes"
            value={place.interior.vibes.join('\n')}
            onChange={(e) => change((p) => void (p.interior.vibes = lines(e.target.value).slice(0, 20)))}
          />,
          'Like Lagos Life’s "Coffee machine queue": short, about what is going on there.',
        )}
        <h3 className="content-subtitle">Spots</h3>
        <p className="admin-muted">
          A spot is a tab in the place’s bar and where the character goes to do its activities.{' '}
          {place.interior.props.length} things stand in this room: move, add and change them with “Design the inside in
          3D”.
        </p>
        <ul className="content-spots">
          {place.interior.spots.map((spot, index) => (
            <li key={index}>
              <Input
                aria-label="Spot icon"
                className="content-icon"
                value={spot.icon}
                maxLength={16}
                onChange={(e) => change((p) => void (p.interior.spots[index]!.icon = e.target.value))}
              />
              <Input
                aria-label="Spot name"
                value={spot.label}
                maxLength={40}
                onChange={(e) => change((p) => void (p.interior.spots[index]!.label = e.target.value))}
              />
              <label className="content-spot-number">
                <span>Across (m)</span>
                <Input
                  type="number"
                  step="0.1"
                  value={spot.x}
                  onChange={(e) => change((p) => void (p.interior.spots[index]!.x = number(e.target.value, spot.x)))}
                />
              </label>
              <label className="content-spot-number">
                <span>Back to front (m)</span>
                <Input
                  type="number"
                  step="0.1"
                  value={spot.z}
                  onChange={(e) => change((p) => void (p.interior.spots[index]!.z = number(e.target.value, spot.z)))}
                />
              </label>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => change((p) => void p.interior.spots.splice(index, 1))}
                  aria-label={`Remove ${spot.label}`}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              change((p) => {
                let n = p.interior.spots.length + 1;
                while (p.interior.spots.some((spot) => spot.id === `spot_${n}`)) n += 1;
                p.interior.spots.push({ id: `spot_${n}`, label: 'New spot', icon: '📍', x: 0, z: 0, activities: [] });
              })
            }
          >
            Add a spot
          </Button>
        )}
      </fieldset>

      <section className="admin-panel content-section" aria-label="Activities">
        <h2 className="content-subtitle">Activities here</h2>
        {!activities.length && <p className="admin-muted">Nothing to do here yet.</p>}
        {activities.map((activity) => (
          <ActivityCard
            key={activity.id}
            activity={activity}
            spots={place.interior.spots.map((spot) => ({ id: spot.id, label: spot.label }))}
            spot={spotOf(activity.id)?.id ?? ''}
            readOnly={readOnly}
            required={(REQUIRED_CONTENT.activities as readonly string[]).includes(activity.id)}
            onChange={(fn) => changeActivity(activity.id, fn)}
            onSpot={(spotId) =>
              change((p) => {
                for (const spot of p.interior.spots)
                  spot.activities = spot.activities.filter((id) => id !== activity.id);
                const target = p.interior.spots.find((spot) => spot.id === spotId);
                if (target) target.activities.push(activity.id);
              })
            }
            onRemove={() =>
              editor.edit((content) => {
                content.activities = content.activities.filter((item) => item.id !== activity.id);
                for (const item of content.places)
                  for (const spot of item.interior.spots)
                    spot.activities = spot.activities.filter((id) => id !== activity.id);
              })
            }
          />
        ))}
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setNewId('');
              setNewLabel('');
              setAdding(true);
            }}
          >
            Add an activity
          </Button>
        )}
      </section>

      <FormDialog
        open={adding}
        title="Add an activity"
        description="It starts free and taking 30 seconds; set its money, effects and spot after."
        submitLabel="Add"
        onSubmit={addActivity}
        onClose={() => setAdding(false)}
      >
        <div className="admin-field">
          <Label htmlFor="activity-id">Id (never changes)</Label>
          <Input id="activity-id" value={newId} maxLength={40} onChange={(e) => setNewId(e.target.value)} />
        </div>
        <div className="admin-field">
          <Label htmlFor="activity-label">Name players see</Label>
          <Input id="activity-label" value={newLabel} maxLength={40} onChange={(e) => setNewLabel(e.target.value)} />
        </div>
      </FormDialog>
      <ConfirmDialog
        open={removing}
        title={`Remove ${place.name}`}
        description={`The place goes from the draft with its ${activities.length} activities. Players keep it until you publish.`}
        confirmLabel="Remove it"
        danger
        onConfirm={async () => {
          editor.edit((content) => {
            content.places = content.places.filter((item) => item.id !== place.id);
            content.activities = content.activities.filter((item) => item.placeId !== place.id);
          });
          onBack();
        }}
        onClose={() => setRemoving(false)}
      />
      <ConfirmDialog
        open={resetting}
        title={`Use the ready-made design for ${place.name}`}
        description="The inside goes back to the game's own design: its things, spots, people, light and colours. Your other changes to the place stay. Players see it when you publish."
        confirmLabel="Use it"
        onConfirm={async () => {
          if (readyMade) change((draft) => void (draft.interior = structuredClone(readyMade)));
        }}
        onClose={() => setResetting(false)}
      />
    </div>
  );
}

function ActivityCard({
  activity,
  spots,
  spot,
  readOnly,
  required,
  onChange,
  onSpot,
  onRemove,
}: {
  activity: ActivityContent;
  spots: { id: string; label: string }[];
  spot: string;
  readOnly: boolean;
  required: boolean;
  onChange: (fn: (draft: ActivityContent) => void) => void;
  onSpot: (spotId: string) => void;
  onRemove: () => void;
}) {
  const key = (name: string) => `${activity.id}-${name}`;
  const skills = Object.entries(activity.skills ?? {}) as [SkillKey, number][];
  return (
    <details className="content-activity">
      <summary>
        <span>
          <span aria-hidden="true">{activity.icon}</span> <strong>{activity.label}</strong>{' '}
          <span className="admin-muted">· {activity.id}</span>
        </span>
        <span className="admin-muted">
          {activity.seconds}s{activity.pricePence !== undefined && ` · £${pounds(activity.pricePence)}`}
          {activity.wagePence !== undefined && ` · earns £${pounds(activity.wagePence)}`}
          {activity.job && ' · job'}
          {activity.opens && ` · opens ${DESKS[activity.opens]}`}
        </span>
      </summary>
      <fieldset className="content-activity-body" disabled={readOnly} aria-label={activity.label}>
        <div className="content-grid">
          <div className="admin-field">
            <Label htmlFor={key('label')}>Name</Label>
            <Input
              id={key('label')}
              value={activity.label}
              maxLength={40}
              onChange={(e) => onChange((a) => void (a.label = e.target.value))}
            />
          </div>
          <div className="admin-field">
            <Label htmlFor={key('icon')}>Icon</Label>
            <Input
              id={key('icon')}
              value={activity.icon}
              maxLength={16}
              onChange={(e) => onChange((a) => void (a.icon = e.target.value))}
            />
          </div>
          <div className="admin-field">
            <Label htmlFor={key('seconds')}>Takes (seconds)</Label>
            <Input
              id={key('seconds')}
              type="number"
              min={1}
              value={activity.seconds}
              onChange={(e) =>
                onChange((a) => void (a.seconds = Math.max(1, Math.round(number(e.target.value, a.seconds)))))
              }
            />
          </div>
          <div className="admin-field">
            <Label htmlFor={key('price')}>Price (£)</Label>
            <Input
              id={key('price')}
              type="number"
              step="0.01"
              min={0}
              value={pounds(activity.pricePence)}
              onChange={(e) => onChange((a) => void (a.pricePence = toPence(e.target.value)))}
            />
          </div>
          <div className="admin-field">
            <Label htmlFor={key('wage')}>Wage (£)</Label>
            <Input
              id={key('wage')}
              type="number"
              step="0.01"
              min={0}
              value={pounds(activity.wagePence)}
              onChange={(e) => onChange((a) => void (a.wagePence = toPence(e.target.value)))}
            />
          </div>
          <div className="admin-field">
            <Label htmlFor={key('energy')}>Needs energy of at least</Label>
            <Input
              id={key('energy')}
              type="number"
              min={0}
              max={100}
              value={activity.minEnergy ?? ''}
              onChange={(e) =>
                onChange((a) => {
                  const value = Math.round(number(e.target.value, 0));
                  a.minEnergy = value > 0 ? Math.min(100, value) : undefined;
                })
              }
            />
          </div>
          <div className="admin-field">
            <Label htmlFor={key('opens')}>Opens a desk</Label>
            <select
              id={key('opens')}
              className="content-select"
              value={activity.opens ?? ''}
              onChange={(e) =>
                onChange(
                  (a) => void (a.opens = e.target.value in DESKS ? (e.target.value as keyof typeof DESKS) : undefined),
                )
              }
            >
              <option value="">No: it takes its time</option>
              <option value="flights">The flights desk (Content → Airport)</option>
              <option value="trains">The ticket office (Content → Trains)</option>
              <option value="ballot">The ballot (Content → Gov)</option>
            </select>
          </div>
          <div className="admin-field">
            <Label htmlFor={key('spot')}>Spot</Label>
            <select id={key('spot')} className="content-select" value={spot} onChange={(e) => onSpot(e.target.value)}>
              <option value="">Not in a spot</option>
              {spots.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="content-mini-title">What it does to needs (−100 to 100)</p>
        <div className="content-needs">
          {NEED_KEYS.map((need: NeedKey) => (
            <label key={need}>
              <span>
                {NEEDS[need].icon} {NEEDS[need].label}
              </span>
              <Input
                type="number"
                min={-100}
                max={100}
                value={activity.effects[need] ?? ''}
                onChange={(e) =>
                  onChange((a) => {
                    const value = Math.round(number(e.target.value, 0));
                    if (value) a.effects[need] = Math.max(-100, Math.min(100, value));
                    else delete a.effects[need];
                  })
                }
              />
            </label>
          ))}
        </div>
        <p className="content-mini-title">Skills it trains (XP a go; the first one also makes it go better)</p>
        <div className="content-needs">
          {SKILL_KEYS.map((skill) => (
            <label key={skill}>
              <span>
                {SKILLS[skill].icon} {SKILLS[skill].label}
              </span>
              <Input
                type="number"
                min={0}
                max={100}
                value={activity.skills?.[skill] ?? ''}
                onChange={(e) =>
                  onChange((a) => {
                    const value = Math.round(number(e.target.value, 0));
                    const next = { ...(a.skills ?? {}) };
                    if (value > 0) next[skill] = Math.min(100, value);
                    else delete next[skill];
                    a.skills = Object.keys(next).length ? next : undefined;
                  })
                }
              />
            </label>
          ))}
        </div>
        {skills.length > 1 && (
          <p className="admin-muted">Makes it go better: {SKILLS[skills[0]![0]].label} (the first one set).</p>
        )}
        <div className="admin-field content-switches">
          <label>
            <Switch
              checked={Boolean(activity.job)}
              onCheckedChange={(on) =>
                onChange(
                  (a) =>
                    void (a.job = on
                      ? { titles: ['Trainee', 'Worker', 'Senior', 'Supervisor', 'Manager'] }
                      : undefined),
                )
              }
            />{' '}
            A job (shifts build a career, with a pay rise each level)
          </label>
        </div>
        {activity.job && (
          <div className="content-titles">
            {activity.job.titles.map((title, index) => (
              <Input
                key={index}
                aria-label={`Career level ${index + 1}`}
                value={title}
                maxLength={40}
                onChange={(e) => onChange((a) => void (a.job!.titles[index] = e.target.value))}
              />
            ))}
          </div>
        )}
        {!readOnly && !required && (
          <Button type="button" variant="ghost" onClick={onRemove}>
            Remove {activity.label}
          </Button>
        )}
        {required && <p className="admin-muted">The game needs this one (it can be changed, not removed).</p>}
      </fieldset>
    </details>
  );
}
